import { Address } from '@dreryk/address-common-api';
import { PractitionerDetails } from '@dreryk/egabinet-personnel-frontend-api';

import { FormRouteLeavingPrompt, FormValidationSnackbar, StyledFlex } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { MyAccountActionBar } from '../../actionBar/MyAccountActionBar';

import { usePersonalDataFormController } from './PersonalDataForm.hooks';
import { StyledPersonalDataForm } from './PersonalDataForm.styled';
import { forDoctor, forParamedic, forSpecialist, getSpecialties, withSpecialties } from './PersonalDataForm.utils';
import { PersonalDataUpdateAction } from './PersonalDataUpdateAction';
import { AddressSection } from './sections/AddressSection/AddressSection';
import { StyledBasicDetailsSection } from './sections/BasicDetailsSection/BasicDetailsSection.styled';
import { EmailAddressInput } from './sections/BasicDetailsSection/EmailAddressInput';
import { FirstNameInput } from './sections/BasicDetailsSection/FirstNameInput';
import { LastNameInput } from './sections/BasicDetailsSection/LastNameInput';
import { MedicalLicenceNumberInput } from './sections/BasicDetailsSection/MedicalLicenceNumberInput';
import { PeselInput } from './sections/BasicDetailsSection/PeselInput';
import { PhoneNumberInput } from './sections/BasicDetailsSection/PhoneAddressInput';
import { ProfessionSelect } from './sections/BasicDetailsSection/ProfessionSelect';
import { TitleInput } from './sections/BasicDetailsSection/TitleInput';
import { DoctorSpecialtiesInteractiveTable } from './sections/DoctorSpecialtiesSection/DoctorSpecialtiesInteractiveTable';
import { DoctorSpecialtiesSection } from './sections/DoctorSpecialtiesSection/DoctorSpecialtiesSection';
import { NoSpecialtyCheckbox } from './sections/DoctorSpecialtiesSection/NoSpecialtyCheckbox';

type PersonalDataFormProps = {
   readonly practitionerDetails: PractitionerDetails;
   readonly onUpdatePersonalData: VoidFunction;
   readonly doctorAddress: Address | undefined;
};

export const PersonalDataForm = (props: PersonalDataFormProps): JSX.Element => {
   const updatingParamedicPersonalDataAvailable = protectFeature('updatingParamedicPersonalData');
   const updatingDoctorPersonalDataAvailable = protectFeature('updatingDoctorPersonalData');

   const controller = usePersonalDataFormController({
      practitionerDetails: props.practitionerDetails,
      updatingDoctorPersonalDataAvailable: updatingDoctorPersonalDataAvailable,
      doctorAddress: props.doctorAddress,
   });

   return (
      <MyAccountActionBar>
         <StyledPersonalDataForm as={'form'} noValidate autoComplete={'off'} direction={'column'} gap={'40px'}>
            <StyledFlex direction={'column'} gap={'12px'}>
               <StyledBasicDetailsSection>
                  <FirstNameInput register={controller.register} />
                  <LastNameInput register={controller.register} />
                  <PhoneNumberInput register={controller.register} />
                  <EmailAddressInput register={controller.register} />
                  {forDoctor(controller, updatingDoctorPersonalDataAvailable) && (
                     <MedicalLicenceNumberInput register={controller.register} />
                  )}
                  {forSpecialist(controller, updatingDoctorPersonalDataAvailable) && (
                     <PeselInput register={controller.register} />
                  )}
                  {forParamedic(controller, updatingParamedicPersonalDataAvailable) && (
                     <ProfessionSelect register={controller.register} />
                  )}
                  <TitleInput register={controller.register} />
               </StyledBasicDetailsSection>

               {forDoctor(controller, updatingDoctorPersonalDataAvailable) && (
                  <DoctorSpecialtiesSection>
                     <NoSpecialtyCheckbox
                        register={controller.register}
                        trigger={controller.trigger}
                        doctorSpecialties={getSpecialties(controller.watch)}
                     />
                     {withSpecialties(controller) && (
                        <DoctorSpecialtiesInteractiveTable register={controller.register} />
                     )}
                  </DoctorSpecialtiesSection>
               )}
            </StyledFlex>
            {forDoctor(controller, updatingDoctorPersonalDataAvailable) ? (
               <AddressSection register={controller.register} watch={controller.watch} />
            ) : null}
         </StyledPersonalDataForm>

         <FormValidationSnackbar formState={controller.formState} />
         <FormRouteLeavingPrompt formState={controller.formState} />
         <PersonalDataUpdateAction
            handleSubmit={controller.handleSubmit}
            onSuccess={props.onUpdatePersonalData}
            updatingDoctorPersonalDataAvailable={updatingDoctorPersonalDataAvailable}
         />
      </MyAccountActionBar>
   );
};
